import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen,
  BookMarked,
  Calculator,
  ChevronDown,
  FileText,
  Menu,
  Phone,
  Search,
  ShoppingCart,
  User,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import MegaMenu from './MegaMenu';
import BrandCard from './BrandCard';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useWebAuth } from '../context/WebAuthContext';
import { api } from '../services/api';
import { trackProductEvent, trackSearch } from '../services/tracking';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

function SectionTitle({ children }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <div className="shrink-0 text-xs font-extrabold text-gray-600">{children}</div>
      <div className="h-px flex-1 bg-gray-300" />
    </div>
  );
}

function SearchResults({ searchData, searching, searchQ, onProductClick, onCategoryClick }) {
  const hasQuery = Boolean((searchQ || '').trim());
  const groupedProducts = useMemo(() => {
    const groups = new Map();
    (searchData.products || []).slice(0, 8).forEach((p) => {
      const slug = p.category_slug || '';
      const key = slug || 'other';
      if (!groups.has(key)) {
        groups.set(key, { slug, title: p.category_title || 'نتایج مرتبط', rows: [] });
      }
      groups.get(key).rows.push(p);
    });
    return Array.from(groups.values());
  }, [searchData.products]);

  return (
    <div className="max-h-[560px] overflow-y-auto overscroll-contain p-2 pt-4" dir="rtl">
      <div>
        <div className="mb-2 text-right text-sm font-bold text-gray-700">نتایج مرتبط</div>
        {!hasQuery ? (
          <div className="rounded-2xl border border-dashed border-blue-100 bg-blue-50/50 px-4 py-6 text-center text-sm font-semibold text-slate-500">
            هنوز عبارتی جستجو نکرده اید
          </div>
        ) : searching ? (
          <div className="text-right text-xs text-gray-500">در حال جستجو...</div>
        ) : groupedProducts.length ? (
          <div className="space-y-3">
            {groupedProducts.map((group) => (
              <section key={group.slug || group.title} className="rounded-2xl border border-gray-100 bg-white p-2">
                <button
                  type="button"
                  onClick={() => onCategoryClick(group.slug)}
                  className="mb-2 w-full rounded-xl bg-slate-50 px-3 py-2 text-right text-xs font-extrabold text-slate-800 hover:bg-blue-50 hover:text-[#005bbb]"
                >
                  {group.title}
                </button>
                <div className="space-y-2">
                  {group.rows.map((p) => (
                    <button
                      key={`p-${p.id}`}
                      type="button"
                      onClick={() => onProductClick(p, 'search_result')}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-right hover:bg-gray-50"
                    >
                      <div className="text-sm font-semibold text-gray-900">{p.title || 'محصول'}</div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="text-right text-xs text-gray-500">موردی پیدا نشد.</div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3" aria-label="بنرها">
          <div className="aspect-[3/1] rounded-2xl border border-black/60 bg-slate-50" />
          <div className="aspect-[3/1] rounded-2xl border border-black/60 bg-slate-50" />
        </div>
        {(
          <div className="mt-4 border-t border-gray-100 pt-4">
            <div className="mb-2 text-right text-sm font-bold text-gray-700">محبوب‌ترین جستجوها</div>
            <div className="flex flex-wrap justify-center gap-2">
              {(searchData.popularProducts || []).slice(0, 4).map((p) => (
                <button
                  key={`pp-${p.id}`}
                  type="button"
                  onClick={() => onProductClick(p, 'popular_search')}
                  className="rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold text-[#005bbb] hover:bg-blue-100"
                >
                  {p.title || 'محصول'}
                </button>
              ))}
              {(searchData.popularProducts || []).length === 0 && (
                <div className="text-xs text-gray-500">موردی ثبت نشده.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthed, user } = useWebAuth();

  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [megaMenuAnchor, setMegaMenuAnchor] = useState(null);
  const [showBrandsMenu, setShowBrandsMenu] = useState(false);
  const [brandsMenuAnchor, setBrandsMenuAnchor] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const categoryButtonRef = useRef(null);
  const brandsButtonRef = useRef(null);
  const searchInputRef = useRef(null);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(128);

  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchData, setSearchData] = useState({
    products: [],
    brands: [],
    categories: [],
    popularQueries: [],
    popularProducts: [],
  });

  const [brands, setBrands] = useState([]);
  const [brandGroups, setBrandGroups] = useState([]);
  const [activeBrandCategoryId, setActiveBrandCategoryId] = useState('all');
  const [brandsLoaded, setBrandsLoaded] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');

  const modalOpen = isSearchOpen || showBrandsMenu || showMegaMenu;
  const isActivePath = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const desktopNavButtonClass = (active) => [
    'inline-flex h-auto min-h-9 items-center gap-2 whitespace-normal border px-3 py-2 hover:text-[#005bbb]',
    active ? 'border-[#005bbb] bg-blue-50 font-extrabold text-[#005bbb] hover:bg-blue-100' : 'border-transparent text-gray-700',
  ].join(' ');
  const mobileNavButtonClass = (active) => [
    'h-16 min-w-0 justify-start gap-2 whitespace-normal rounded-lg border px-3 py-2 text-[13px] font-semibold leading-5 hover:text-[#005bbb] [&_svg]:size-5',
    active
      ? 'border-[#005bbb] bg-blue-50 font-extrabold text-[#005bbb] hover:bg-blue-100'
      : 'border-slate-200 bg-white text-gray-700',
  ].join(' ');

  useBodyScrollLock(modalOpen);

  useEffect(() => {
    const element = headerRef.current;
    if (!element) return undefined;
    const measure = () => {
      const height = Math.ceil(element.getBoundingClientRect().height);
      setHeaderHeight(height);
      document.documentElement.style.setProperty('--pp-header-height', `${height}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setIsSearchOpen(false);
    setShowMegaMenu(false);
    setMegaMenuAnchor(null);
    setShowBrandsMenu(false);
    setBrandsMenuAnchor(null);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!isSearchOpen) return undefined;
    let mounted = true;
    (async () => {
      setSearching(true);
      try {
        const res = await api.searchProducts('');
        if (mounted) setSearchData(res?.data || res || searchData);
      } catch {
        if (mounted) setSearchData({ products: [], brands: [], categories: [], popularQueries: [], popularProducts: [] });
      } finally {
        if (mounted) setSearching(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) return undefined;
    const q = String(searchQ || '').trim();
    if (!q) return undefined;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.searchProducts(q);
        const nextData = res?.data || res || { products: [], brands: [], categories: [], popularQueries: [], popularProducts: [] };
        setSearchData(nextData);
        trackSearch(q, Array.isArray(nextData.products) ? nextData.products.length : 0, { path: window.location.pathname + window.location.search });
      } catch {
        setSearchData({ products: [], brands: [], categories: [], popularQueries: [], popularProducts: [] });
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ, isSearchOpen]);

  const displayName = useMemo(() => {
    if (!user) return '';
    return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.phone || '';
  }, [user]);

  async function ensureBrands() {
    if (brandsLoaded) return;
    try {
      const [brandsRes, groupsRes] = await Promise.all([
        api.getBrands(),
        api.getBrandGroups().catch(() => ({ data: [] })),
      ]);
      setBrands(brandsRes?.data || []);
      setBrandGroups(groupsRes?.data || []);
    } catch {
      setBrands([]);
      setBrandGroups([]);
    } finally {
      setBrandsLoaded(true);
    }
  }

  const filteredBrands = useMemo(() => {
    const q = brandSearch.trim().toLowerCase();
    if (!q) return brands || [];
    return (brands || []).filter((brand) => {
      const name = String(brand.name || brand.title || '').toLowerCase();
      const slug = String(brand.slug || '').toLowerCase();
      return name.includes(q) || slug.includes(q);
    });
  }, [brands, brandSearch]);

  const featuredBrands = useMemo(() => (brands || []).filter((brand) => brand.is_featured), [brands]);

  const activeBrandGroup = useMemo(
    () => (brandGroups || []).find((group) => String(group.id) === String(activeBrandCategoryId)) || null,
    [brandGroups, activeBrandCategoryId]
  );

  const categoryBrands = useMemo(() => {
    if (!activeBrandGroup) return [];
    const ids = new Set((activeBrandGroup.brands || []).map((brand) => String(brand.id)));
    return filteredBrands.filter((brand) => ids.has(String(brand.id)));
  }, [activeBrandGroup, filteredBrands]);

  const shownBrands = activeBrandGroup ? categoryBrands : filteredBrands;

  function openSearch() {
    setShowMegaMenu(false);
    setShowBrandsMenu(false);
    setIsSearchOpen(true);
    requestAnimationFrame(() => searchInputRef.current?.focus?.());
  }

  function closeSearch() {
    setIsSearchOpen(false);
  }

  function submitSearch(e) {
    e.preventDefault();
    const q = searchQ.trim();
    if (!q) return;
    openSearch();
    trackSearch(q, Array.isArray(searchData.products) ? searchData.products.length : 0, { path: window.location.pathname + window.location.search });
  }

  function productListingPath(product) {
    return `/product/${product?.id}`;
  }

  function goSearchProduct(product, source) {
    trackProductEvent('product_click', {
      product_item_id: product.id,
      source,
      query: searchQ || '',
      metadata: { title: product.name || product.title || '', sku: product.sku || '' },
    });
    closeSearch();
    navigate(productListingPath(product));
  }

  function goSearchCategory(slug) {
    if (!slug) return;
    closeSearch();
    navigate(`/subcategory/${slug}`);
  }

  function openHeaderCart() {
    window.dispatchEvent(new Event('open-preinvoice-cart'));
  }

  return (
    <>
      <header
        ref={headerRef}
        className={`${isSearchOpen ? 'bg-transparent shadow-sm' : 'bg-[#FAF9F5] shadow-sm'} fixed inset-x-0 top-0 w-full overflow-visible md:sticky ${isSearchOpen ? 'z-[80]' : 'z-50'}`}
        dir="rtl"
        onMouseDown={() => {
          if (isSearchOpen) closeSearch();
        }}
      >
        {isSearchOpen && (
          <div className="fixed inset-0 z-[70] bg-slate-950/45 backdrop-blur-[3px]" onMouseDown={closeSearch} />
        )}

        <div className={`relative ${isSearchOpen ? 'bg-transparent' : 'bg-[#FAF9F5]'}`}>
          <div className="pp-container">
            <div className="flex h-16 items-center justify-between gap-4 border-b border-black/35">
              <Link to="/" className={`flex items-center gap-3 whitespace-nowrap transition ${isSearchOpen ? 'opacity-35 blur-[2px]' : ''}`}>
                <img src="/images/parspiping-logo.svg" alt="پارس پایپینگ" className="h-8 w-auto shrink-0 object-contain md:h-11" />
              </Link>

              <form
                onSubmit={submitSearch}
                className={`relative hidden min-w-0 flex-1 md:block ${isSearchOpen ? 'z-[90]' : ''}`}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {isSearchOpen && <div aria-hidden="true" className="pointer-events-none absolute -inset-x-[9px] -top-[9px] bottom-0 rounded-t-2xl border border-[#005bbb] border-b-0 bg-white" />}
                <div
                  className={[
                    'relative flex h-11 items-center overflow-hidden border bg-white transition-[background-color,border-color,border-radius] duration-200 ease-out',
                    isSearchOpen ? 'relative z-10 rounded-2xl border-black/60 bg-slate-200/80' : 'rounded-full border-black/60 hover:border-[#005bbb]',
                  ].join(' ')}
                >
                  <Search className="mr-4 h-5 w-5 shrink-0 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQ}
                    onFocus={openSearch}
                    onClick={openSearch}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="جستجو در محصولات"
                    className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-gray-800 outline-none placeholder:text-gray-400"
                  />
                  {searchQ ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQ('');
                        searchInputRef.current?.focus?.();
                      }}
                      className={`ml-1 flex h-9 w-9 shrink-0 items-center justify-center text-slate-600 transition hover:bg-slate-300/70 ${isSearchOpen ? 'rounded-[12px]' : 'rounded-2xl'}`}
                      aria-label="پاک کردن جستجو"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                  <button type="submit" className="hidden">جستجو</button>
                </div>

                {isSearchOpen && (
                  <div className="absolute -right-[9px] top-full z-[5] w-[calc(100%+18px)] overflow-hidden rounded-b-2xl border border-[#005bbb] border-t-0 bg-white px-2 pb-2">
                    <SearchResults
                      searchData={searchData}
                      searching={searching}
                      searchQ={searchQ}
                      onProductClick={goSearchProduct}
                      onCategoryClick={goSearchCategory}
                    />
                  </div>
                )}
              </form>

              <div className={`mr-auto flex items-center gap-3 transition ${isSearchOpen ? 'opacity-35 blur-[2px]' : ''}`}>
                <Button variant="ghost" size="icon" className="rounded-full md:hidden" onClick={openSearch}>
                  <Search className="h-5 w-5 text-gray-700" />
                </Button>

                <button
                  type="button"
                  onClick={openHeaderCart}
                  className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100"
                  aria-label="سبد پیش‌فاکتور"
                >
                  <ShoppingCart className="h-5 w-5" />
                </button>

                <button
                  type="button"
                  className={`group relative h-11 min-w-[152px] ${isAuthed ? 'pr-10 pl-0' : 'pl-10 pr-0'}`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    if (isAuthed) navigate('/account');
                    else navigate(`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`);
                  }}
                >
                  <span aria-hidden="true" className={`absolute inset-y-0 z-0 rounded-full transition ${isAuthed ? 'left-8 right-0 bg-emerald-600 group-hover:bg-emerald-700' : 'left-0 right-8 bg-[#005bbb] group-hover:bg-[#004a99]'}`} />
                  <span className="relative z-10 flex h-11 min-w-[124px] items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3 text-[#1f2937] shadow-sm transition group-hover:border-[#005bbb]/30 group-hover:bg-blue-50">
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-slate-100 text-slate-400">
                      <User className="h-4 w-4" />
                    </span>
                    <span className="max-w-[150px] whitespace-normal break-words text-center text-sm font-black leading-4">
                      {isAuthed ? (displayName || 'حساب کاربری') : 'ورود ثبت‌نام'}
                    </span>
                  </span>
                </button>
              </div>

              <a href="tel:02112345678" dir="ltr" className={`hidden items-center gap-2 whitespace-nowrap text-sm font-bold text-gray-800 transition hover:text-[#005bbb] sm:flex ${isSearchOpen ? 'opacity-35 blur-[2px]' : ''}`}>
                <Phone className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                <span>021-12345678</span>
              </a>
            </div>
          </div>
        </div>

        <div className={`relative border-b border-black/35 ${isSearchOpen ? 'bg-transparent' : 'bg-[#FAF9F5]'}`}>
          <div className="pp-container">
            <div className={`flex flex-wrap items-center gap-3 py-2 transition ${isSearchOpen ? 'opacity-35 blur-[2px]' : ''}`}>
              <div className="flex items-center gap-2">
                <Button
                  ref={categoryButtonRef}
                  onClick={() => {
                    setShowBrandsMenu(false);
                    setBrandsMenuAnchor(null);
                    setMegaMenuAnchor(categoryButtonRef.current?.getBoundingClientRect?.() || null);
                    setShowMegaMenu((v) => !v);
                  }}
                  className={[
                    'flex items-center gap-2 rounded-full border border-black/60 px-4 text-[#005bbb] hover:bg-[#005bbb] hover:text-white sm:px-6',
                    showMegaMenu ? 'bg-[#005bbb] text-white shadow-xl shadow-slate-900/10' : 'bg-[#005bbb]/5',
                  ].join(' ')}
                >
                  <Menu className="h-5 w-5" />
                  <span className="text-sm font-medium">دسته‌بندی محصولات</span>
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-white/70 text-current">
                    <ChevronDown className="h-4 w-4" />
                  </span>
                </Button>

                <Button
                  type="button"
                  ref={brandsButtonRef}
                  onClick={() => {
                    ensureBrands();
                    setShowMegaMenu(false);
                    setMegaMenuAnchor(null);
                    setBrandsMenuAnchor(brandsButtonRef.current?.getBoundingClientRect?.() || null);
                    setBrandSearch('');
                    setActiveBrandCategoryId('all');
                    setShowBrandsMenu((v) => !v);
                  }}
                  className={[
                    'flex items-center gap-2 rounded-full border border-black/60 px-4 text-sm text-[#005bbb] hover:bg-[#005bbb] hover:text-white sm:px-5',
                    showBrandsMenu ? 'bg-[#005bbb] text-white shadow-xl shadow-slate-900/10' : 'bg-[#005bbb]/5',
                  ].join(' ')}
                >
                  <span className="font-medium">برندها</span>
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-slate-100/80 text-current">
                    <ChevronDown className="h-4 w-4" />
                  </span>
                </Button>
              </div>

              <nav className="mt-2 flex w-full flex-wrap items-center gap-3 text-sm text-gray-700 md:mt-0 md:w-auto">
                <Link to="/about" onClick={() => { setShowMegaMenu(false); setShowBrandsMenu(false); }} className="transition-colors hover:text-[#005bbb]">
                  درباره ما
                </Link>
                <Link to="/#customers" onClick={() => { setShowMegaMenu(false); setShowBrandsMenu(false); }} className="transition-colors hover:text-[#005bbb]">
                  مشتریان
                </Link>
                <Link to="/contact" onClick={() => { setShowMegaMenu(false); setShowBrandsMenu(false); }} className="transition-colors hover:text-[#005bbb]">
                  تماس با ما
                </Link>
              </nav>

              <div className="mr-auto hidden items-center gap-2 md:flex">
                <Button variant="ghost" size="sm" className={desktopNavButtonClass(isActivePath('/engineering-library'))} onClick={() => navigate('/engineering-library')}>
                  <BookMarked className="h-5 w-5" />
                  <span className="text-sm">دانشنامه فنی پایپینگ</span>
                </Button>
                <Button variant="ghost" size="sm" className={desktopNavButtonClass(isActivePath('/product-catalogs'))} onClick={() => navigate('/product-catalogs')}>
                  <BookOpen className="h-5 w-5" />
                  <span className="text-sm">کاتالوگ محصولات</span>
                </Button>
                <Button variant="ghost" size="sm" className={desktopNavButtonClass(isActivePath('/weight-calculator'))} onClick={() => navigate('/weight-calculator')}>
                  <Calculator className="h-5 w-5" />
                  <span className="text-sm">محاسبه وزن</span>
                </Button>
                <Button variant="ghost" size="sm" className={desktopNavButtonClass(isActivePath('/preinvoice'))} onClick={() => navigate('/preinvoice')}>
                  <FileText className="h-5 w-5" />
                  <span className="text-sm">پیش‌فاکتور</span>
                </Button>
              </div>

              <div className="grid w-full grid-cols-2 gap-2 md:hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  className={mobileNavButtonClass(isActivePath('/engineering-library'))}
                  aria-current={isActivePath('/engineering-library') ? 'page' : undefined}
                  onClick={() => navigate('/engineering-library')}
                >
                  <BookMarked className="h-5 w-5 shrink-0 text-[#005bbb]" />
                  <span className="min-w-0 flex-1 whitespace-normal text-right">دانشنامه فنی پایپینگ</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={mobileNavButtonClass(isActivePath('/product-catalogs'))}
                  aria-current={isActivePath('/product-catalogs') ? 'page' : undefined}
                  onClick={() => navigate('/product-catalogs')}
                >
                  <BookOpen className="h-5 w-5 shrink-0 text-[#005bbb]" />
                  <span className="min-w-0 flex-1 whitespace-normal text-right">کاتالوگ محصولات</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={mobileNavButtonClass(isActivePath('/weight-calculator'))}
                  aria-current={isActivePath('/weight-calculator') ? 'page' : undefined}
                  onClick={() => navigate('/weight-calculator')}
                >
                  <Calculator className="h-5 w-5 shrink-0 text-[#005bbb]" />
                  <span className="min-w-0 flex-1 whitespace-normal text-right">محاسبه وزن</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={mobileNavButtonClass(isActivePath('/preinvoice'))}
                  aria-current={isActivePath('/preinvoice') ? 'page' : undefined}
                  onClick={() => navigate('/preinvoice')}
                >
                  <FileText className="h-5 w-5 shrink-0 text-[#005bbb]" />
                  <span className="min-w-0 flex-1 whitespace-normal text-right">پیش‌فاکتور</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div style={{ height: headerHeight }} className="md:hidden" aria-hidden="true" />

      {isSearchOpen && (
        <div className="fixed inset-x-4 bottom-3 z-[90] overflow-y-auto overscroll-contain md:hidden" style={{ top: headerHeight + 8 }} dir="rtl">
          <div className="mx-auto w-full max-w-2xl rounded-2xl bg-white p-4 shadow-2xl">
            <div className="mb-4 flex items-center gap-4">
              <Search className="h-6 w-6 text-gray-400" />
              <input
                type="text"
                placeholder="جستجو در محصولات"
                className="min-w-0 flex-1 text-right text-sm outline-none"
                autoFocus
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
              />
              <button onClick={() => setIsSearchOpen(false)} className="rounded-xl p-2 text-gray-400 hover:bg-slate-100 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <SearchResults searchData={searchData} searching={searching} searchQ={searchQ} onProductClick={goSearchProduct} onCategoryClick={goSearchCategory} />
          </div>
        </div>
      )}

      {showBrandsMenu && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 overscroll-contain"
          style={{ top: Math.max((brandsMenuAnchor?.bottom || headerHeight), headerHeight) }}
          dir="rtl"
          onClick={() => setShowBrandsMenu(false)}
        >
          <div className="absolute inset-0 bg-slate-950/35 backdrop-blur-[3px]" />
          <div
            className="absolute bottom-0 top-0 flex flex-col overflow-hidden overscroll-contain border border-gray-200 bg-white shadow-2xl"
            style={{ right: 0, width: Math.min(1188, window.innerWidth), maxWidth: '100vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <h2 className="text-base font-extrabold text-gray-900 sm:text-lg">برندها</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowBrandsMenu(false)} className="rounded-xl text-slate-500 hover:bg-slate-200 hover:text-slate-900">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div dir="ltr" data-brands-scroll="outer" className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-gray-50/80 px-6 py-5">
              <div dir="rtl">
              <div className="mb-5">
                <SectionTitle>دسته‌بندی‌های اصلی</SectionTitle>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveBrandCategoryId('all')}
                    className={`rounded-2xl border px-3 py-2 text-xs font-bold transition ${activeBrandCategoryId === 'all' ? 'border-[#005bbb] bg-blue-50 text-[#005bbb]' : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200'}`}
                  >
                    همه برندها
                  </button>
                  {(brandGroups || []).map((group) => (
                    <button
                      type="button"
                      key={group.id}
                      onClick={() => setActiveBrandCategoryId(group.id)}
                      className={`rounded-2xl border px-3 py-2 text-xs font-bold transition ${String(activeBrandCategoryId) === String(group.id) ? 'border-[#005bbb] bg-blue-50 text-[#005bbb]' : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200'}`}
                    >
                      {group.title}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <SectionTitle>{activeBrandGroup ? `برندهای مرتبط با ${activeBrandGroup.title}` : 'کلیه برندها'}</SectionTitle>
                <div data-brands-scroll="inner" className="h-[360px] overflow-y-auto overscroll-contain sm:h-[390px]">
                <div className="grid grid-cols-2 items-start gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {shownBrands.map((brand) => (
                    <BrandCard
                      key={brand.id}
                      brand={brand}
                      onClick={() => {
                        setShowBrandsMenu(false);
                        if (brand?.slug) navigate(`/brand/${brand.slug}`);
                      }}
                    />
                  ))}
                  {brandsLoaded && shownBrands.length === 0 && (
                    <div className="col-span-full py-8 text-center text-xs text-gray-500">
                      برندی با این جست‌وجو پیدا نشد.
                    </div>
                  )}
                </div>
                </div>
              </div>

              <div className="relative mb-5">
                <Search className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  placeholder="جست‌وجوی برند..."
                  className="w-full rounded-2xl border border-gray-200 bg-white py-3 pr-11 pl-4 text-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              {featuredBrands.length > 0 && (
                <div>
                  <SectionTitle>برندهای منتخب</SectionTitle>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
                    {featuredBrands.map((brand) => (
                      <BrandCard
                        key={`featured-${brand.id}`}
                        brand={brand}
                        onClick={() => {
                          setShowBrandsMenu(false);
                          if (brand?.slug) navigate(`/brand/${brand.slug}`);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showMegaMenu && (
        <MegaMenu
          anchorRect={{ ...megaMenuAnchor, bottom: Math.max(megaMenuAnchor?.bottom || headerHeight, headerHeight) }}
          onClose={() => {
            setShowMegaMenu(false);
            setMegaMenuAnchor(null);
          }}
        />
      )}
    </>
  );
};

export default Header;
